import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { Button } from '@/components/ui/button';
import { useDialerTelephony } from '@/contexts/DialerTelephonyContext';
import { smsCapableNumbers, smsSenderOrAuto } from '@/services/dialer/smsSender';
import { formatDateTime } from '@/i18n/locale';
import { threadMessages, replySender, smsDelivery, type StoredSms } from '@/services/dialer/smsThread';

/**
 * One SMS conversation, opened from the list on the left (Kevin, 2026-10-07):
 * only this contact's messages, oldest at the top and the newest at the
 * bottom, above a field that sends on Enter. The "Envoyer un SMS" form is not
 * shown while a conversation is open: replying happens here.
 */

interface SmsThreadProps {
  /** The conversation's number, as the message store writes it. */
  phone: string;
  name?: string;
}

export default function SmsThread({ phone, name }: SmsThreadProps) {
  const { t } = useTranslation(['pages']);
  const queryClient = useQueryClient();
  const { selectedCallerId, activeCallerNumbers } = useDialerTelephony() as {
    selectedCallerId?: string | null;
    activeCallerNumbers?: Array<{ phoneNumber?: string | null; capabilities?: string[] | null }>;
  };
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: stored = [], isLoading, refetch } = useQuery({
    queryKey: ['sms-thread', phone],
    queryFn: async () => {
      const res = await api.functions.invoke('listInboundSms', { phone, limit: 200 });
      return ((res?.data as { messages?: StoredSms[] } | undefined)?.messages) || [];
    },
    refetchInterval: 10000,
  });
  const messages = useMemo(() => threadMessages(stored, phone), [stored, phone]);

  // The newest message is the one to read: keep the bottom in view.
  const lastKey = messages[messages.length - 1]?.key;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [lastKey, phone]);
  useEffect(() => { setDraft(''); }, [phone]);

  const capable = smsCapableNumbers(activeCallerNumbers);
  const from = replySender(messages, capable) ?? smsSenderOrAuto(selectedCallerId, activeCallerNumbers);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await api.functions.invoke('twilioSendSMS', { to: phone, message: text, from });
      setDraft('');
      await refetch();
      void queryClient.invalidateQueries({ queryKey: ['sms-threads'] });
    } catch (error) {
      const e = error as { data?: { error?: string; message?: string }; message?: string };
      const detail = e?.data?.error || e?.data?.message || e?.message || t('pages:smsPanel.toasts.unknownError');
      toast.error(t('pages:smsPanel.toasts.sendFailed', { detail }));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="bg-card border border-border/50 rounded-xl flex flex-col h-[70vh] min-h-[24rem]"
      data-testid="conversation-sms"
    >
      <div className="px-4 py-3 border-b border-border/50">
        <p className="text-sm font-semibold text-foreground truncate">{name || phone}</p>
        {name && <p className="text-xs text-muted-foreground font-mono truncate">{phone}</p>}
      </div>

      <div className="flex-1 overflow-y-auto vf-scroll-discreet px-4 py-3 space-y-2">
        {isLoading && (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <MessageSquare className="w-8 h-8 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">{t('pages:sms.noMessages')}</p>
          </div>
        )}
        {messages.map((msg) => {
          const inbound = msg.direction === 'inbound';
          return (
            <div key={msg.key} className={`flex ${inbound ? 'justify-start' : 'justify-end'}`}>
              <div
                className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${
                  inbound ? 'bg-muted text-foreground rounded-bl-sm' : 'bg-[var(--vf-primary)] text-white rounded-br-sm'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                <div className={`text-[10px] mt-0.5 flex items-center gap-2 ${inbound ? 'text-muted-foreground' : 'text-white/70'}`}>
                  <span>{msg.at ? formatDateTime(msg.at) : ''}</span>
                  {!inbound && smsDelivery(msg.status) && (
                    <span className={smsDelivery(msg.status) === 'failed' ? 'font-semibold text-red-200' : ''}>
                      {t(`pages:sms.delivery.${smsDelivery(msg.status)}`)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form
        className="border-t border-border/50 p-3 flex items-end gap-2"
        onSubmit={(e) => { e.preventDefault(); void send(); }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter goes to the next line.
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send();
            }
          }}
          rows={1}
          placeholder={t('pages:sms.placeholder')}
          aria-label={t('pages:sms.placeholder')}
          disabled={capable.length === 0}
          className="flex-1 resize-none max-h-32 min-h-[2.5rem] rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button
          type="submit"
          size="icon"
          disabled={!draft.trim() || sending || capable.length === 0}
          aria-label={t('pages:sms.send')}
          title={t('pages:sms.send')}
          className="h-10 w-10 shrink-0 bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)]"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
      {capable.length === 0 && (
        <p className="px-3 pb-3 text-xs text-muted-foreground">{t('pages:smsPanel.empty.noSmsNumber.title')}</p>
      )}
    </div>
  );
}

